/**
 * Wire-контракт замеров поиска (`Platform.Contracts/Testing/SearchTestingContracts.cs`, RP-FB28) и его проверка на
 * границе: неожиданная форма — `invalid-response`, а не пустой экран. Числа сходства и долей — дробные.
 */
import { fail, flag, integer, list, optionalText, record, text, textList, textMap } from '../../../shared/decode'
import type {
  TestingCheck,
  TestingFrameOutcome,
  TestingLane,
  TestingLaneMetrics,
  TestingMean,
  TestingProportion,
  TestingRun,
  TestingRunDetail,
  TestingRunMetrics,
  TestingRunStamp,
  TestPhoto,
  TestProduct,
  TestProductDetail,
  TextCheck,
  TextQuery,
  TextQueryKind,
  TextQueryOutcome,
  TextRun,
  TextRunDetail,
  TextRunMetrics,
} from '../model/types'
import { isTestingLane, isTestingRunState, isTextQueryKind, isTextSearchStep } from '../model/types'

export function num(value: unknown, field: string): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fail(`поле ${field} — не число`)
}

export function optionalNum(value: unknown, field: string): number | null {
  return value === null || value === undefined ? null : num(value, field)
}

/** Строка, которая может быть пустой (имя товара-соперника, которого уже нет). */
export function anyText(value: unknown, field: string): string {
  return typeof value === 'string' ? value : fail(`поле ${field} — не строка`)
}

function lane(value: unknown, field: string): TestingLane {
  return typeof value === 'string' && isTestingLane(value) ? value : fail(`поле ${field} — неизвестная дорожка`)
}

function decodeProduct(raw: unknown, name = 'product'): TestProduct {
  const item = record(raw, name)
  const source = item.source === null || item.source === undefined ? null : record(item.source, `${name}.source`)
  const state = text(item.indexState, `${name}.indexState`)
  return {
    id: text(item.id, `${name}.id`),
    name: text(item.name, `${name}.name`),
    sku: optionalText(item.sku, `${name}.sku`),
    barcodes: textList(item.barcodes, `${name}.barcodes`),
    source: source === null ? null : { productId: text(source.productId, `${name}.source.productId`), copiedAt: text(source.copiedAt, `${name}.source.copiedAt`) },
    references: integer(item.references, `${name}.references`),
    frames: integer(item.frames, `${name}.frames`),
    indexState: state === 'indexed' ? 'indexed' : 'failed',
    indexError: optionalText(item.indexError, `${name}.indexError`),
    version: integer(item.version, `${name}.version`),
    updatedAt: text(item.updatedAt, `${name}.updatedAt`),
  }
}

function decodePhoto(raw: unknown, name: string): TestPhoto {
  const item = record(raw, name)
  return {
    id: text(item.id, `${name}.id`),
    role: text(item.role, `${name}.role`) === 'frame' ? 'frame' : 'reference',
    origin: text(item.origin, `${name}.origin`) === 'source' ? 'source' : 'manual',
    contentType: text(item.contentType, `${name}.contentType`),
    bytes: integer(item.bytes, `${name}.bytes`),
    tags: textMap(item.tags ?? {}, `${name}.tags`),
    addedAt: text(item.addedAt, `${name}.addedAt`),
  }
}

export function decodeTestProducts(raw: unknown): TestProduct[] {
  return list(raw, 'products', decodeProduct)
}

export function decodeTestProductDetail(raw: unknown): TestProductDetail {
  const body = record(raw, 'detail')
  return {
    product: decodeProduct(body.product, 'detail.product'),
    references: list(body.references, 'detail.references', decodePhoto),
    frames: list(body.frames, 'detail.frames', decodePhoto),
  }
}

export function decodeTestPhoto(raw: unknown): TestPhoto {
  return decodePhoto(raw, 'photo')
}

export function decodeTestingCheck(raw: unknown): TestingCheck {
  const body = record(raw, 'check')
  const recognized = body.recognized === null || body.recognized === undefined ? null : record(body.recognized, 'check.recognized')
  return {
    expectedProductId: optionalText(body.expectedProductId, 'check.expectedProductId'),
    recognized:
      recognized === null
        ? null
        : { text: optionalText(recognized.text, 'check.recognized.text'), barcodes: textList(recognized.barcodes ?? [], 'check.recognized.barcodes') },
    lanes: list(body.lanes, 'check.lanes', (entry, name) => {
      const item = record(entry, name)
      return {
        lane: lane(item.lane, `${name}.lane`),
        expectedRank: optionalNum(item.expectedRank, `${name}.expectedRank`),
        expectedScore: optionalNum(item.expectedScore, `${name}.expectedScore`),
        margin: optionalNum(item.margin, `${name}.margin`),
        hits: list(item.hits, `${name}.hits`, (hitRaw, hitName) => {
          const hit = record(hitRaw, hitName)
          return {
            rank: integer(hit.rank, `${hitName}.rank`),
            productId: text(hit.productId, `${hitName}.productId`),
            name: anyText(hit.name, `${hitName}.name`),
            confidence: num(hit.confidence, `${hitName}.confidence`),
            confidenceParts: list(hit.confidenceParts ?? [], `${hitName}.confidenceParts`, (partRaw, partName) => {
              const part = record(partRaw, partName)
              return { lane: text(part.lane, `${partName}.lane`), points: num(part.points, `${partName}.points`), evidence: num(part.evidence, `${partName}.evidence`) }
            }),
            points: num(hit.points, `${hitName}.points`),
            matchedBy: textList(hit.matchedBy, `${hitName}.matchedBy`),
          }
        }),
      }
    }),
    latencyMs: num(body.latencyMs, 'check.latencyMs'),
  }
}

export function proportion(raw: unknown, name: string): TestingProportion {
  const item = record(raw, name)
  return {
    successes: integer(item.successes, `${name}.successes`),
    n: integer(item.n, `${name}.n`),
    value: optionalNum(item.value, `${name}.value`),
    low: optionalNum(item.low, `${name}.low`),
    high: optionalNum(item.high, `${name}.high`),
  }
}

export function mean(raw: unknown, name: string): TestingMean {
  const item = record(raw, name)
  return {
    n: integer(item.n, `${name}.n`),
    value: optionalNum(item.value, `${name}.value`),
    low: optionalNum(item.low, `${name}.low`),
    high: optionalNum(item.high, `${name}.high`),
  }
}

/** Баллы дорожки (или замера по тексту целиком): те же доли и средние, что считает сервер. */
function laneScores(item: Record<string, unknown>, name: string): Omit<TestingLaneMetrics, 'lane'> {
  return {
    frames: integer(item.frames, `${name}.frames`),
    top1: proportion(item.top1, `${name}.top1`),
    top3: proportion(item.top3, `${name}.top3`),
    top5: proportion(item.top5, `${name}.top5`),
    top10: proportion(item.top10, `${name}.top10`),
    notFound: proportion(item.notFound, `${name}.notFound`),
    mrr: mean(item.mrr, `${name}.mrr`),
    positionScore: mean(item.positionScore, `${name}.positionScore`),
    expectedScore: mean(item.expectedScore, `${name}.expectedScore`),
    margin: mean(item.margin, `${name}.margin`),
  }
}

function decodeMetrics(raw: unknown): TestingRunMetrics {
  const body = record(raw, 'metrics')
  const latency = record(body.latency, 'metrics.latency')
  return {
    lanes: list(body.lanes, 'metrics.lanes', (entry, name) => {
      const item = record(entry, name)
      return { lane: lane(item.lane, `${name}.lane`), ...laneScores(item, name) }
    }),
    products: list(body.products, 'metrics.products', (entry, name) => {
      const item = record(entry, name)
      return {
        productId: text(item.productId, `${name}.productId`),
        name: anyText(item.name, `${name}.name`),
        frames: integer(item.frames, `${name}.frames`),
        top1: proportion(item.top1, `${name}.top1`),
        top5: proportion(item.top5, `${name}.top5`),
        positionScore: mean(item.positionScore, `${name}.positionScore`),
      }
    }),
    tags: list(body.tags, 'metrics.tags', (entry, name) => {
      const item = record(entry, name)
      return {
        axis: text(item.axis, `${name}.axis`),
        value: text(item.value, `${name}.value`),
        frames: integer(item.frames, `${name}.frames`),
        top1: proportion(item.top1, `${name}.top1`),
        top5: proportion(item.top5, `${name}.top5`),
        positionScore: mean(item.positionScore, `${name}.positionScore`),
      }
    }),
    confusions: list(body.confusions, 'metrics.confusions', (entry, name) => {
      const item = record(entry, name)
      return {
        expectedProductId: text(item.expectedProductId, `${name}.expectedProductId`),
        expectedName: anyText(item.expectedName, `${name}.expectedName`),
        insteadProductId: text(item.insteadProductId, `${name}.insteadProductId`),
        insteadName: anyText(item.insteadName, `${name}.insteadName`),
        count: integer(item.count, `${name}.count`),
      }
    }),
    latency: {
      n: integer(latency.n, 'metrics.latency.n'),
      medianMs: optionalNum(latency.medianMs, 'metrics.latency.medianMs'),
      p95Ms: optionalNum(latency.p95Ms, 'metrics.latency.p95Ms'),
    },
    errors: integer(body.errors, 'metrics.errors'),
  }
}

function decodeStamp(raw: unknown): TestingRunStamp {
  const body = record(raw, 'stamp')
  return {
    encoderModel: optionalText(body.encoderModel, 'stamp.encoderModel'),
    encoderDim: optionalNum(body.encoderDim, 'stamp.encoderDim'),
    platformVersion: text(body.platformVersion, 'stamp.platformVersion'),
    rrfK: num(body.rrfK, 'stamp.rrfK'),
    visualWeight: num(body.visualWeight, 'stamp.visualWeight'),
    textWeight: num(body.textWeight, 'stamp.textWeight'),
    barcodeWeight: num(body.barcodeWeight, 'stamp.barcodeWeight'),
    products: integer(body.products, 'stamp.products'),
    frames: integer(body.frames, 'stamp.frames'),
    confidenceFormula: optionalText(body.confidenceFormula, 'stamp.confidenceFormula'),
    photoPrep: optionalText(body.photoPrep, 'stamp.photoPrep'),
  }
}

function decodeRunBody(raw: unknown, name = 'run'): TestingRun {
  const item = record(raw, name)
  const state = text(item.state, `${name}.state`)
  if (!isTestingRunState(state)) fail(`поле ${name}.state — неизвестное состояние`)
  return {
    id: text(item.id, `${name}.id`),
    state,
    lanes: text(item.lanes, `${name}.lanes`),
    createdAt: text(item.createdAt, `${name}.createdAt`),
    startedAt: optionalText(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
    totalFrames: integer(item.totalFrames, `${name}.totalFrames`),
    doneFrames: integer(item.doneFrames, `${name}.doneFrames`),
    error: optionalText(item.error, `${name}.error`),
    createdBy: anyText(item.createdBy, `${name}.createdBy`),
    stamp: item.stamp === null || item.stamp === undefined ? null : decodeStamp(item.stamp),
    metrics: item.metrics === null || item.metrics === undefined ? null : decodeMetrics(item.metrics),
  }
}

export function decodeTestingRun(raw: unknown): TestingRun {
  return decodeRunBody(raw)
}

export function decodeTestingRuns(raw: unknown): TestingRun[] {
  return list(raw, 'runs', (entry, name) => decodeRunBody(entry, name))
}

export function decodeComparison(raw: unknown): TestingRunDetail['comparison'] {
  if (raw === null || raw === undefined) return null
  const comparison = record(raw, 'runDetail.comparison')
  return {
    previousRunId: text(comparison.previousRunId, 'comparison.previousRunId'),
    sameStamp: flag(comparison.sameStamp, 'comparison.sameStamp'),
    deltas: list(comparison.deltas, 'comparison.deltas', (entry, name) => {
      const item = record(entry, name)
      return {
        metric: text(item.metric, `${name}.metric`),
        previous: optionalNum(item.previous, `${name}.previous`),
        current: optionalNum(item.current, `${name}.current`),
        delta: optionalNum(item.delta, `${name}.delta`),
      }
    }),
  }
}

export function decodeTestingRunDetail(raw: unknown): TestingRunDetail {
  const body = record(raw, 'runDetail')
  return { run: decodeRunBody(body.run, 'runDetail.run'), comparison: decodeComparison(body.comparison) }
}

export function decodeTestingFrameOutcomes(raw: unknown): TestingFrameOutcome[] {
  return list(raw, 'frames', (entry, name) => {
    const item = record(entry, name)
    return {
      frameId: text(item.frameId, `${name}.frameId`),
      productId: text(item.productId, `${name}.productId`),
      productName: anyText(item.productName, `${name}.productName`),
      tags: textMap(item.tags ?? {}, `${name}.tags`),
      recognizedText: optionalText(item.recognizedText, `${name}.recognizedText`),
      latencyMs: optionalNum(item.latencyMs, `${name}.latencyMs`),
      error: optionalText(item.error, `${name}.error`),
      lanes: list(item.lanes, `${name}.lanes`, (laneRaw, laneName) => {
        const outcome = record(laneRaw, laneName)
        return {
          lane: lane(outcome.lane, `${laneName}.lane`),
          rank: optionalNum(outcome.rank, `${laneName}.rank`),
          score: optionalNum(outcome.score, `${laneName}.score`),
          margin: optionalNum(outcome.margin, `${laneName}.margin`),
          topProductId: optionalText(outcome.topProductId, `${laneName}.topProductId`),
          topName: optionalText(outcome.topName, `${laneName}.topName`),
        }
      }),
    }
  })
}

// --- поиск по тексту (`TextSearchTestingContracts.cs`, RP-FB34) -------------------------------------------------

function queryKind(value: unknown, field: string): TextQueryKind {
  return typeof value === 'string' && isTextQueryKind(value) ? value : fail(`поле ${field} — неизвестный вид запроса`)
}

function decodeQuery(raw: unknown, name: string): TextQuery {
  const item = record(raw, name)
  return {
    id: text(item.id, `${name}.id`),
    text: text(item.text, `${name}.text`),
    kind: queryKind(item.kind, `${name}.kind`),
    origin: text(item.origin, `${name}.origin`) === 'generated' ? 'generated' : 'manual',
    productId: text(item.productId, `${name}.productId`),
    productName: anyText(item.productName, `${name}.productName`),
    createdAt: text(item.createdAt, `${name}.createdAt`),
  }
}

export function decodeTextQueries(raw: unknown): TextQuery[] {
  return list(raw, 'queries', decodeQuery)
}

export function decodeTextQuery(raw: unknown): TextQuery {
  return decodeQuery(raw, 'query')
}

export function decodeGeneratedQueries(raw: unknown): { removed: number; created: number; queries: TextQuery[] } {
  const body = record(raw, 'generated')
  return {
    removed: integer(body.removed, 'generated.removed'),
    created: integer(body.created, 'generated.created'),
    queries: list(body.queries, 'generated.queries', decodeQuery),
  }
}

export function decodeTextCheck(raw: unknown): TextCheck {
  const body = record(raw, 'textCheck')
  const step = text(body.step, 'textCheck.step')
  return {
    query: anyText(body.query, 'textCheck.query'),
    expectedProductId: optionalText(body.expectedProductId, 'textCheck.expectedProductId'),
    step: isTextSearchStep(step) ? step : fail('поле textCheck.step — неизвестная ступень'),
    expectedRank: optionalNum(body.expectedRank, 'textCheck.expectedRank'),
    expectedMatchedBy: optionalText(body.expectedMatchedBy, 'textCheck.expectedMatchedBy'),
    hits: list(body.hits, 'textCheck.hits', (entry, name) => {
      const hit = record(entry, name)
      return {
        rank: integer(hit.rank, `${name}.rank`),
        productId: text(hit.productId, `${name}.productId`),
        name: anyText(hit.name, `${name}.name`),
        sku: optionalText(hit.sku, `${name}.sku`),
        matchedBy: optionalText(hit.matchedBy, `${name}.matchedBy`),
      }
    }),
    latencyMs: num(body.latencyMs, 'textCheck.latencyMs'),
  }
}

function decodeOutcome(raw: unknown, name: string): TextQueryOutcome {
  const item = record(raw, name)
  return {
    queryId: text(item.queryId, `${name}.queryId`),
    text: anyText(item.text, `${name}.text`),
    kind: anyText(item.kind, `${name}.kind`),
    productId: text(item.productId, `${name}.productId`),
    productName: anyText(item.productName, `${name}.productName`),
    step: optionalText(item.step, `${name}.step`),
    matchedBy: optionalText(item.matchedBy, `${name}.matchedBy`),
    rank: optionalNum(item.rank, `${name}.rank`),
    above: list(item.above, `${name}.above`, (entry, entryName) => anyText(entry, entryName)),
    latencyMs: optionalNum(item.latencyMs, `${name}.latencyMs`),
    error: optionalText(item.error, `${name}.error`),
  }
}

function decodeTextMetrics(raw: unknown): TextRunMetrics {
  const body = record(raw, 'textMetrics')
  const latency = record(body.latency, 'textMetrics.latency')
  return {
    overall: laneScores(record(body.overall, 'textMetrics.overall'), 'textMetrics.overall'),
    kinds: list(body.kinds, 'textMetrics.kinds', (entry, name) => {
      const item = record(entry, name)
      return {
        kind: text(item.kind, `${name}.kind`),
        queries: integer(item.queries, `${name}.queries`),
        top1: proportion(item.top1, `${name}.top1`),
        top5: proportion(item.top5, `${name}.top5`),
        notFound: proportion(item.notFound, `${name}.notFound`),
        positionScore: mean(item.positionScore, `${name}.positionScore`),
      }
    }),
    matches: list(body.matches, 'textMetrics.matches', (entry, name) => {
      const item = record(entry, name)
      return { matchedBy: text(item.matchedBy, `${name}.matchedBy`), queries: integer(item.queries, `${name}.queries`) }
    }),
    worst: list(body.worst, 'textMetrics.worst', decodeOutcome),
    latency: {
      n: integer(latency.n, 'textMetrics.latency.n'),
      medianMs: optionalNum(latency.medianMs, 'textMetrics.latency.medianMs'),
      p95Ms: optionalNum(latency.p95Ms, 'textMetrics.latency.p95Ms'),
    },
    errors: integer(body.errors, 'textMetrics.errors'),
  }
}

function decodeTextRunBody(raw: unknown, name = 'textRun'): TextRun {
  const item = record(raw, name)
  const state = text(item.state, `${name}.state`)
  if (!isTestingRunState(state)) fail(`поле ${name}.state — неизвестное состояние`)
  const stamp = item.stamp === null || item.stamp === undefined ? null : record(item.stamp, `${name}.stamp`)
  return {
    id: text(item.id, `${name}.id`),
    state,
    createdAt: text(item.createdAt, `${name}.createdAt`),
    startedAt: optionalText(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
    totalQueries: integer(item.totalQueries, `${name}.totalQueries`),
    doneQueries: integer(item.doneQueries, `${name}.doneQueries`),
    error: optionalText(item.error, `${name}.error`),
    createdBy: anyText(item.createdBy, `${name}.createdBy`),
    stamp:
      stamp === null
        ? null
        : {
            platformVersion: text(stamp.platformVersion, `${name}.stamp.platformVersion`),
            textGeneration: optionalText(stamp.textGeneration, `${name}.stamp.textGeneration`),
            products: integer(stamp.products, `${name}.stamp.products`),
            queries: integer(stamp.queries, `${name}.stamp.queries`),
          },
    metrics: item.metrics === null || item.metrics === undefined ? null : decodeTextMetrics(item.metrics),
  }
}

export function decodeTextRun(raw: unknown): TextRun {
  return decodeTextRunBody(raw)
}

export function decodeTextRuns(raw: unknown): TextRun[] {
  return list(raw, 'textRuns', (entry, name) => decodeTextRunBody(entry, name))
}

export function decodeTextRunDetail(raw: unknown): TextRunDetail {
  const body = record(raw, 'textRunDetail')
  return { run: decodeTextRunBody(body.run, 'textRunDetail.run'), comparison: decodeComparison(body.comparison) }
}
